import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { fetchLibrary, type LibraryItem } from '@/data/sources-library';
import { channelName, formatDay } from './libraryModel';
import { ItemStatus } from './LibraryRows';
import { BlogEditorDialog } from './LibraryDialogs';

/* Create · AI Studio (content_library_v1 ON): the two long lists become "Recent 6
 * + View all in Library" (design §4.2). Same domain scope as the Library default. */
export function StudioRecent() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { active, select } = useActiveDomain();
  const projectId = active?.projectId ?? null;
  const [blogOpen, setBlogOpen] = useState<{ id: number; projectId: number | null } | null>(null);
  const recent = useQuery({
    queryKey: ['content-library', { recent: true, project_id: projectId }],
    queryFn: () => fetchLibrary({ view: 'all', limit: '6', project_id: String(projectId) }),
    enabled: projectId != null,
  });
  const open = (it: LibraryItem) => {
    if (it.kind === 'blog') {
      if (it.status === 'ready') setBlogOpen({ id: it.id, projectId: it.project_id ?? projectId });
      else navigate('/social/library?v=all&type=blog');
      return;
    }
    if (it.project_id && it.project_id !== projectId) void select(it.project_id);
    navigate('/social/content-studio/' + it.id);
  };
  return (
    <section className="mt-8 space-y-3" aria-label="Recent content">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-text-primary">Recent content</h2>
        <Link to="/social/library" className="text-sm font-medium text-ai hover:underline">View all in Library →</Link>
      </div>
      <p className="text-xs text-text-secondary">Your latest blogs and social posts{active ? ` for ${active.displayName || active.hostname}` : ''}. Filter by date, page, keyword or channel in the Library.</p>
      {projectId == null ? (
        <EmptyState title="Choose a domain to see its recent content" teach="Select a domain to view at the top of the app, or open the Library and set Site to All domains." />
      ) : recent.isLoading ? <LoadingState rows={3} /> : recent.isError ? <ErrorState error={recent.error} onRetry={() => void recent.refetch()} /> : (recent.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="Nothing here yet" teach="Start from a format above, or ask Aura to write a blog. New items appear here and in the Library." />
      ) : (
        <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle bg-surface">
          {recent.data!.items.map((it) => (
            <li key={it.kind + it.id}>
              <button onClick={() => open(it)} className="flex min-h-[48px] w-full min-w-0 items-center gap-3 px-3 py-2 text-left hover:bg-hover/40">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-text-primary">{it.title || 'Untitled'}</span>
                  <span className="block truncate text-[11px] text-text-secondary">{it.kind === 'blog' ? `Blog${it.children.length ? ` · ${it.children.length} social` : ''}` : channelName(it.channel)} · {formatDay(it.updated_at)}</span>
                </span>
                <ItemStatus item={it} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {blogOpen && <BlogEditorDialog id={blogOpen.id} projectId={blogOpen.projectId} onClose={() => setBlogOpen(null)} onChanged={() => void qc.invalidateQueries({ queryKey: ['content-library'] })} />}
    </section>
  );
}
